import type { Quote, QuoteEffectiveStatus } from '../types'

/**
 * Quote status helpers (spec 008, R6). `expired` is never stored: a sent or
 * viewed quote whose expiry date has passed READS as expired, mirroring the
 * SQL `quote_effective_status()` / `quote_is_open()` pair so the arborist,
 * the client and the database can never disagree.
 *
 * Dates are compared as ISO calendar strings (YYYY-MM-DD), which is what the
 * `date` column serialises to, so no timezone arithmetic is involved.
 */

type StatusInput = Pick<Quote, 'status' | 'expiry_date'>

/** Today's calendar date in the device's local zone as YYYY-MM-DD. */
export function localISODate(now: Date = new Date()): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const isSentOrViewed = (status: Quote['status']) => status === 'sent' || status === 'viewed'

export function effectiveQuoteStatus(
  q: StatusInput,
  today: string = localISODate()
): QuoteEffectiveStatus {
  if (isSentOrViewed(q.status) && q.expiry_date && q.expiry_date < today) {
    return 'expired'
  }
  return q.status
}

/** Open for a client decision: sent|viewed and today ≤ expiry (INV-Q6). */
export function isQuoteOpen(q: StatusInput, today: string = localISODate()): boolean {
  return isSentOrViewed(q.status) && !!q.expiry_date && q.expiry_date >= today
}

/** Everything except a draft can be revised into a new version (FR-018). */
export function canRevise(status: QuoteEffectiveStatus): boolean {
  return status !== 'draft'
}

export const QUOTE_STATUS_LABEL: Record<QuoteEffectiveStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Viewed',
  accepted: 'Accepted',
  declined: 'Declined',
  superseded: 'Superseded',
  expired: 'Expired',
}

export type QuoteStatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

export const QUOTE_STATUS_TONE: Record<QuoteEffectiveStatus, QuoteStatusTone> = {
  draft: 'neutral',
  sent: 'info',
  viewed: 'info',
  accepted: 'success',
  declined: 'danger',
  superseded: 'neutral',
  expired: 'warning',
}
